import {
  UseMutationResult,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { useSession } from '../SessionProvider';
import {
  ExportOutputFormat,
  getReport,
  getReportExportFormats,
  getReportTemplates,
  ParsedReportParams,
  ReportTemplate,
} from './getReports';
import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';
import {
  CloneReport,
  CreateReportTemplateAndReport,
  ReportBackendTypes,
  ReportTypeToParams,
  UpdateScheduleForReportTemplate,
} from './createReports';
import { deleteReport, deleteReportTemplate } from './deleteReports';
import { convertFieldsToDates } from '@ais-ui/utils';

export const useGetReportTemplates = (
  shouldRefetch: boolean
): UseQueryResult<ReportTemplate[]> => {
  const { dbSession } = useSession();
  return useQuery(
    ['getReportTemplates'],
    async () => {
      if (!dbSession) {
        throw new Error('dbSession is not defined.');
      }

      const result = await getReportTemplates(dbSession); // Ensure async function

      if (result) {
        for (const template of result) {
          convertFieldsToDates(template, [
            'tplnewestcreationdate',
            'jobs.creationdate',
            'schedule.endTime',
          ]);
        }
      }

      return result;
    },
    // We do not want to refetch all the time as it causes re-renders
    {
      refetchInterval: shouldRefetch ? 2000 : false,
    }
  );
};

export const useGetReport = (
  jobId: string,
  timeZone: string
): UseQueryResult<ParsedReportParams> => {
  const { dbSession } = useSession();
  return useQuery(['getReport', jobId], async () => {
    if (!dbSession) {
      throw new Error('dbSession is not defined.');
    }
    const result = await getReport(dbSession, jobId);

    if (result) {
      convertFieldsToDates(result, [
        'info.Created',
        'info.End time',
        'info.Start time',
      ]);
    }

    return result;
  });
};

export const useGetReportExportFormats = (
  type: string
): UseQueryResult<ExportOutputFormat[]> => {
  const { dbSession } = useSession();

  return useQuery(['getReportExportFormats', type], () => {
    if (!dbSession) {
      throw new Error('dbSession is not defined.');
    }
    return getReportExportFormats(dbSession, type);
  });
};

type ReportInput<T extends ReportBackendTypes> = {
  type: T;
  params: ReportTypeToParams[T];
};

export const useCreateReportTemplateAndReport = <
  T extends ReportBackendTypes
>(): UseMutationResult<string, Error, ReportInput<T>, unknown> => {
  const { dbSession } = useSession();
  const queryClient = useQueryClient();

  return useMutation<string, Error, ReportInput<T>>(
    ({ type, params }) => {
      if (!dbSession) {
        throw new Error('dbSession is not defined.');
      }
      return CreateReportTemplateAndReport(dbSession, type, params);
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['getReportTemplates']),
    }
  );
};

export const useDisableReportSchedule = (): UseMutationResult<
  string,
  Error,
  object,
  unknown
> => {
  const { dbSession } = useSession();
  const queryClient = useQueryClient();

  return useMutation<string, Error, object>(
    (params) => {
      if (!dbSession) {
        throw new Error('dbSession is not defined.');
      }
      return UpdateScheduleForReportTemplate(dbSession, params);
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['getReportTemplates']),
    }
  );
};

export const useCloneReport = <
  T extends ReportBackendTypes
>(): UseMutationResult<string, Error, ReportInput<T>, unknown> => {
  const { dbSession } = useSession();
  const queryClient = useQueryClient();

  return useMutation<string, Error, ReportInput<T>>(
    ({ type, params }) => {
      if (!dbSession) {
        throw new Error('dbSession is not defined.');
      }
      return CloneReport(dbSession, type, params);
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['getReportTemplates']),
    }
  );
};

export const useDeleteReportTemplate = (): UseMutationResult<
  null,
  Error,
  string,
  unknown
> => {
  const { dbSession } = useSession();
  const queryClient = useQueryClient();

  return useMutation(
    (uuid: string) => {
      if (!dbSession) {
        throw new Error('dbSession is not defined.');
      }
      return deleteReportTemplate(dbSession, uuid);
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['getReportTemplates']),
    }
  );
};

export const useDeleteReport = (): UseMutationResult<
  null,
  Error,
  string,
  unknown
> => {
  const { dbSession } = useSession();
  const queryClient = useQueryClient();

  return useMutation(
    (uuid: string) => {
      if (!dbSession) {
        throw new Error('dbSession is not defined.');
      }
      return deleteReport(dbSession, uuid);
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['getReportTemplates']),
    }
  );
};
